import { motion } from 'framer-motion';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Column,
  FormField,
  InputSelectField,
  InputTextField,
  Row,
  Tooltip,
  Typography,
} from '@tempmee/react';
import { OutlineInformationCircleIcon, SolidCheckCircleIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { VERIFY_PARTNERSHIP_CODE, verifyPartnershipCode } from '../../../../../actions';
import useLoading from '../../../../../hooks/useLoading';
import { useOfficeInformation } from '../hooks/useOfficeInformation';

const partnershipCodeVariants = {
  show: { opacity: 1, maxHeight: 200 },
  hide: { opacity: 0, maxHeight: 0 },
};

export const PartnershipField = () => {
  const dispatch = useDispatch();
  const { setValue, control } = useFormContext();
  const { formValues } = useOfficeInformation(control);
  const selectedChildOffice = useSelector((state) => state.user.selectedChildOffice);
  const partnershipOrganizations = useSelector((state) => state.user.partnershipOrganizations);
  const { isLoading } = useLoading([VERIFY_PARTNERSHIP_CODE]);

  const handleVerify = () => {
    dispatch(
      verifyPartnershipCode({
        code: formValues.partnershipCode,
        organization: formValues.partnershipOrganization,
      }),
    );
  };

  return (
    <Column gap={3}>
      {/* Association header */}
      <Row align="center" gap={1}>
        <Typography bold size="body_large" style={{ flex: '1 1 100%' }}>
          Select which Dental Association or state this office is affiliated with, if applicable.
        </Typography>
        <Tooltip content="GoTu is strategically partnered with various Dental Associations. If you are affiliated with any of the following associations, you may be eligible to have your booking fee refunded on your first completed shift.">
          <OutlineInformationCircleIcon
            width={20}
            height={20}
            color={colors.neutral['300']}
            style={{ cursor: 'pointer' }}
          />
        </Tooltip>
      </Row>

      {/* Organization selector */}
      {selectedChildOffice?.partnerReferral ? (
        <Typography style={{ marginLeft: 20 }}>
          {formValues.partnershipOrganization}
        </Typography>
      ) : (
        <InputSelectField
          value={formValues.partnershipOrganization}
          onChange={(value) => setValue('partnershipOrganization', value)}
          options={
            partnershipOrganizations?.map((item) => ({ label: item, value: item })) ?? []
          }
        />
      )}

      {/* Partnership code */}
      {formValues.partnershipOrganization && (
        <motion.div
          initial={selectedChildOffice?.partnerReferral ? 'show' : 'hide'}
          animate={formValues.partnershipOrganization ? 'show' : 'hide'}
          variants={partnershipCodeVariants}
          transition={{ duration: 0.3 }}
        >
          <Column gap={3} style={{ marginTop: 24 }}>
            <FormField label="Partner Discount Code:" optional>
              {selectedChildOffice?.partnerReferral ? (
                <Row align="center" gap={2}>
                  <Typography style={{ marginLeft: 20 }}>
                    {formValues.partnershipCode}
                  </Typography>
                  <SolidCheckCircleIcon
                    width={20}
                    height={20}
                    color={colors.success['500']}
                  />
                </Row>
              ) : (
                <InputTextField
                  value={formValues.partnershipCode}
                  placeholder="Enter Code"
                  onChange={(e) => setValue('partnershipCode', e.target.value)}
                  disabled={selectedChildOffice?.partnerReferral}
                />
              )}
            </FormField>

            {!selectedChildOffice?.partnerReferral && (
              <Row justify="center" style={{ marginTop: 24 }}>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={!formValues.partnershipCode}
                  onClick={handleVerify}
                  isLoading={isLoading}
                >
                  Apply Code
                </Button>
              </Row>
            )}
          </Column>
        </motion.div>
      )}
    </Column>
  );
};
